export default {
  en: {
    pageTitle: 'Policies',
    empty: {
      title: 'No policies',
      subtitle: 'Add a policy to apply requirements to suppliers',
    },
    addEditPage: {
      newPolicy: 'New policy',
      saveAdd: 'Save policy',
      saveEdit: 'Save changes',
      learnMore: 'Learn more',
      errors: {
        alreadyExists: "There's already a policy with that name.",
        loading: 'There was an error loading the policy.',
        loadingTemplate: 'There was an error loading the policy template.',
        revertTargetChanged:
          "This policy's template has been updated. Check the requirements and save again.",
        saving: 'There was an error when trying to save the policy.',
        emptyMultiselect: 'The policy has required fields that are empty.',
        invalid: 'Validation failed. Please check your changes and try again.',
      },
    },
    requirementsLock: {
      heading: 'Policy requirements',
      locked:
        "Risk Ledger manages and periodically updates these policy requirements. If you unlock to make your own changes, you won't receive future updates to them.",
      unlock: 'Unlock requirements',
      lockedTooltip: 'These requirements are locked. Unlock them to make changes.',
      unlockModal: {
        title: 'Unlock requirements?',
        description:
          "You'll be able to change this policy's requirements. Once you save your changes, you won't receive future updates to this pre-made policy.",
        confirm: 'Unlock requirements',
        cancel: 'Cancel',
      },
      unlockUnsaved:
        "You've unlocked these requirements. They'll stop following the template when you save.",
      relock: 'Re-lock requirements',
      relockModal: {
        title: 'Discard requirement changes?',
        description:
          "Re-locking brings back the template's requirements and discards the changes you've made to them.",
        confirm: 'Re-lock requirements',
        cancel: 'Keep editing',
      },
      unlocked:
        "Your organisation has modified these policy requirements from the {0} policy template. You can revert these rules back to link to the template's rules.",
      unlockedWithoutName:
        'Your organisation has modified these policy requirements from their policy template.',
      revert: 'Revert policy requirements',
      revertModal: {
        title: 'Revert to template requirements?',
        description:
          "This replaces the policy's requirements with those from the {0} policy template and locks them again. Your organisation's changes to them are removed when you save.",
        confirm: 'Revert requirements',
        cancel: 'Cancel',
      },
    },
    controlRow: {
      new: 'New',
      updated: 'Updated',
      newTooltip: 'Risk Ledger recently introduced this control',
      updatedTooltip: 'Risk Ledger recently updated this control',
    },
    row: {
      lastModified: 'Last modified by {0} on {1}',
      appliesToSuppliers: '{n} supplier | {n} suppliers',
      supplierList: '{0} and {1} more ...',
      edit: 'Edit',
      delete: 'Delete',
      duplicate: 'Duplicate',
      export: 'Export to CSV',
      modal: {
        title: 'Are you sure you want to delete the policy "{0}"?',
        description:
          "This policy's requirements will stop being applied to {n} supplier | This policy's requirements will stop being applied to {n} suppliers",
        confirm: 'Delete',
        cancel: 'Cancel',
        banner: 'There was a problem while trying to delete the policy',
      },
    },
    controlTypes: {
      nocompliant: 'Require no',
      yescompliant: 'Require yes',
      atleast: 'Require at least',
      atmax: 'Require at most',
    },
    filters: {
      title: 'Filter by',
      placeholder: 'Search by control name...',
      required: 'Required',
      notRequired: 'Not required',
      requiredTitle: 'Required',
      frameworkSizeTitle: 'Framework size',
      domainsTitle: 'Domains',
    },
    form: {
      details: 'Policy details',
      name: 'Name',
      nameRequired: 'Enter a policy name',
      description: 'Description',
      appliesTo: {
        title: 'Who should this policy apply to?',
        required: 'Select at least one classification or label',
        description:
          'Suppliers with any of these classifications or labels applied will be impacted by this policy',
        supplierCount:
          'Impacting none of your suppliers | Impacting 1 of your suppliers | Impacting {n} of your suppliers',
      },
      criticality: 'Criticality',
      selectCriticality: 'Select criticality',
      confidentiality: 'Confidentiality',
      selectConfidentiality: 'Select confidentiality',
      pii: 'PII',
      selectPii: 'Select PII',
      labels: 'Labels',
    },
    controlList: {
      noFilteredAnswers: 'No controls match the filters applied',
      noFilteredAnswersNote: 'Change the filters to show some controls',
      rule: 'Control rule',
      requirement: 'Requirement',
      policyOptions: 'Policy options',
      setRequirementFor: 'Set a requirement for',
      noRequirement: 'No requirement set',
      requireEvidence: 'Require uploaded evidence',
      requireNotes: 'Require written explanation',
      validation: {
        title: 'Validation',
        requireEvidence: 'Require evidence',
        requireNotes: 'Require notes',
        none: 'No validation set',
      },
    },
    export: {
      button: 'Export to CSV',
      errors: {
        invalid: 'That policy could not be exported.',
        forbidden: "You don't have permission to export this policy.",
        generic: 'There was a problem exporting the policy. Try again.',
      },
    },
    import: {
      errors: {
        empty: "The CSV file doesn't contain a policy.",
        header: "The CSV file doesn't match the expected policy format.",
        malformed: 'The CSV file is malformed.',
        generic: 'There was a problem reading the CSV file. Try again.',
      },
    },
    listPage: {
      title: 'Policies',
      addPolicy: 'Add a policy',
      description:
        "Policies set the control requirements your suppliers must meet and shape which questions they're asked. Build one from a template, from scratch, or by mapping to an external framework.",
      descriptionWithoutTemplates:
        "Policies set the control requirements your suppliers must meet and shape which questions they're asked. Build one from scratch or upload one from a CSV.",
      filters: {
        entityName: 'policies',
        searchPlaceholder: 'Search policies',
        tags: 'Tags',
        labels: 'Labels',
      },
      columns: {
        name: 'Policy name',
        appliesTo: 'Applies to',
        suppliers: 'Suppliers',
        actions: 'Actions',
      },
      error: 'Failed to load policies. Try again or contact Risk Ledger support.',
      noResultsTitle: 'No matching policies',
      noResultsSubtitle: 'Try a different search or filter',
      createFromScratch: 'Create from scratch',
      addFromTemplate: 'Add a pre-made policy',
      uploadCsv: 'Upload from a CSV',
    },
    templatePicker: {
      title: 'Add a pre-made policy',
      intro: "Select a pre-made policy, from industry frameworks to Risk Ledger's default policies",
      frameworks: 'Industry frameworks',
      riskLedger: 'Risk Ledger default policies',
      controlCount: '{n} controls | {n} control | {n} controls',
      updated: 'Updated {date}',
      empty: 'There are no pre-made policies available yet',
      cancel: 'Cancel',
      submit: 'Add policy',
      required: 'Select a pre-made policy',
    },
    tagLabelSelector: {
      tags: 'Tags',
      labels: 'Labels',
      newLabel: 'Create new label',
    },
    policyMatrix: {
      title: 'Policy matrix',
      policiesRoot: 'Policies',
      export: 'Export',
      domainColumn: 'Domain',
      controlColumn: 'Control',
      empty: 'You have no policies',
      noResultsTitle: 'No matching controls',
      noResultsSubtitle: 'Try a different search or filter',
      filename: 'riskledger-policy-matrix',
      entityName: 'controls',
      searchPlaceholder: 'Search controls',
    },
  },
};
